/**
 * PWA install nudge — a small, dismissible floating card that offers to install
 * the app to the home screen / desktop.
 *
 * How it works:
 *   - On supporting browsers (Chromium, Edge, Android) the `beforeinstallprompt`
 *     event fires; we capture + defer it, then surface an "Installer l'app"
 *     button. Clicking it replays the native prompt.
 *   - On iOS Safari there is no such event, so we show a short "Partager → Sur
 *     l'écran d'accueil" hint instead (only when not already installed).
 *   - Dismissing (or a successful install) is remembered in localStorage so the
 *     nudge does not nag on every visit.
 *
 * It renders as a `fixed` element anchored bottom-right and sits *below* modals
 * (z-[10000]) and toasts, so it never disrupts the existing layout. Fully
 * theme-aware via the app's semantic tokens; progressive-enhancement only —
 * absence of the API simply means no card is shown.
 */

import { useEffect, useState } from 'react';
import { useT } from '@/i18n';

/** Minimal shape of the (non-standard) beforeinstallprompt event. */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt: () => Promise<void>;
}

const DISMISS_KEY = 'psn-pwa-install-dismissed';

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mm = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mm || iosStandalone;
}

function isIos(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ reports as Mac; detect touch to catch it.
  const iPadOS = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return (iOSDevice || iPadOS) && isSafari;
}

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* storage unavailable — nudge simply reappears next session */
  }
}

export default function PwaInstallPrompt() {
  const { t } = useT();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(() => wasDismissed());

  useEffect(() => {
    if (dismissed || isStandalone()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault(); // stop Chrome's default mini-infobar
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setShowIosHint(false);
      setDismissed(true);
      rememberDismissed();
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS never fires beforeinstallprompt — offer the manual hint instead.
    if (isIos()) setShowIosHint(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [dismissed]);

  const close = () => {
    setDismissed(true);
    setDeferred(null);
    setShowIosHint(false);
    rememberDismissed();
  };

  const install = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch {
      /* user gesture lost or prompt already consumed — ignore */
    } finally {
      setDeferred(null);
      setDismissed(true);
      rememberDismissed();
    }
  };

  if (dismissed) return null;
  const visible = deferred !== null || showIosHint;
  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label={t('shell.components.pwaInstallPrompt.installTheApp')}
      className="fixed inset-x-4 bottom-4 z-[9000] mx-auto max-w-sm rounded-token border border-border bg-surface p-4 text-foreground shadow-elevation-3 sm:inset-x-auto sm:right-4 sm:left-auto"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-token bg-primary/10 text-primary"
        >
          {/* share / network glyph, matching the app icon */}
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
            <circle cx="6" cy="6.5" r="2.6" />
            <circle cx="6" cy="17.5" r="2.6" />
            <circle cx="17.5" cy="12" r="2.6" />
            <path
              d="M7.6 7.6l8.3 3.2M7.6 16.4l8.3-3.2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{t('shell.components.pwaInstallPrompt.installKarexia')}</p>
          {deferred ? (
            <p className="mt-0.5 text-xs text-muted">{t('shell.components.pwaInstallPrompt.reachYourContactsAndCampaignsFaster')}</p>
          ) : (
            <p className="mt-0.5 text-xs text-muted">{t('shell.components.pwaInstallPrompt.onIphoneIpadTap')}{' '}
              <span className="font-medium text-foreground">{t('shell.components.pwaInstallPrompt.share')}</span>{' '}{t('shell.components.pwaInstallPrompt.then')}{' '}
              <span className="font-medium text-foreground">{t('shell.components.pwaInstallPrompt.addToHomeScreen')}</span>.
            </p>
          )}

          {deferred && (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={install}
                className="inline-flex items-center gap-1.5 rounded-token bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >{t('shell.components.pwaInstallPrompt.installTheApp2')}</button>
              <button
                type="button"
                onClick={close}
                className="rounded-token px-3 py-1.5 text-xs font-medium text-muted transition hover:bg-surface-2 hover:text-foreground"
              >{t('shell.components.pwaInstallPrompt.later')}</button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={close}
          aria-label={t('shell.components.pwaInstallPrompt.close')}
          className="-mr-1 -mt-1 shrink-0 rounded-token p-1 text-muted transition hover:bg-surface-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
